import { State, appendOrMoveToIndexWith, appendValue, moveTo } from '@btime/state-monad'
import { assoc, compose, curry, dissoc, map, pathEq, pick, prop, propEq, propOr, uniq } from 'ramda'
import { sortReplies, uniqId } from '@btime/helpers'
import React from 'react'

import { hasValue } from '../../../../utils/helpers/value'
import { useChecklist } from '../_load/ChecklistLoader'
import { useMaxQuestions } from './useMaxQuestions'

const parseQuestionData = data => {
  if (data == null) return {}
  if (typeof data === 'object') return data
  try {
    return typeof data === 'string' ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

const formatChildDisplayConditions = question => {
  if (question.questionType.name === 'block' || question.children.every(child => !hasValue(child.displayConditions)))
    return question
  if (question.questionType?.name === 'checkbox') {
    return assoc(
      'childDisplayConditions',
      [[{ field: 'value', operator: 'eq', value: true }], [{ field: 'value', operator: 'eq', value: false }]],
      question
    )
  }
  return assoc('childDisplayConditions', uniq(question.children.map(prop('displayConditions'))), question)
}

const normalizeAutofillForForm = autofill => {
  if (!autofill || typeof autofill !== 'object') return autofill
  const source = autofill.source != null ? String(autofill.source).toLowerCase() : autofill.source
  const fieldId = autofill.fieldId ?? autofill.field_id ?? null
  return { ...autofill, source, fieldId, field_id: fieldId }
}

const formatConditions = (conditions, relatedQuestion) => {
  if (!conditions?.length || !relatedQuestion) return conditions

  return conditions.map(condition => {
    if (condition.operator !== 'within_refs') return condition

    return {
      ...condition,
      value: relatedQuestion.options.filter(option => condition.value.includes(option.reference))
    }
  })
}

const formatQuestion = curry((allQuestions, question) => {
  const children = allQuestions.filter(propEq('parentReference', question.reference))
  const parentQuestion = allQuestions.find(propEq('reference', question.parentReference))

  return compose(
    formatChildDisplayConditions,
    question => assoc('displayConditions', formatConditions(question.displayConditions, parentQuestion), question),
    question =>
      assoc('nonComplianceConditions', formatConditions(question.nonComplianceConditions, question), question),
    question => assoc('children', map(formatQuestion(allQuestions), children), question),

    dissoc('type'),
    question => assoc('questionType', question.type, question),
    question => assoc('reference', question.reference || uniqId(), question),
    question => {
      const dataParsed = parseQuestionData(question.data)
      const fromTopLevel = question.autofill ? normalizeAutofillForForm(question.autofill) : null
      const fromDataAutofill = dataParsed?.autofill
      const mergedAutofill =
        fromTopLevel != null
          ? {
              ...fromTopLevel,
              ...(fromDataAutofill && pick(['editable', 'updateBase', 'update_base'], fromDataAutofill))
            }
          : fromDataAutofill != null
            ? { ...normalizeAutofillForForm(fromDataAutofill), ...pick(['editable', 'updateBase', 'update_base'], fromDataAutofill) }
            : null
      if (mergedAutofill == null) return question
      return assoc(
        'data',
        { ...dataParsed, autofill: mergedAutofill },
        question.autofill ? dissoc('autofill', question) : question
      )
    }
  )(question)
})

const formatCategories = allQuestions => {
  return allQuestions.reduce((categories, question) => {
    if (!!question.parentReference) return categories

    const category = propOr({}, 'category', question)
    const stateCategory = {
      selectedCategory: category,
      children: [],
      reference: uniqId()
    }

    return State.of(categories)
      .map(appendOrMoveToIndexWith(pathEq(['selectedCategory', 'id'], category.id), stateCategory))
      .map(moveTo('children'))
      .map(appendValue(formatQuestion(allQuestions, question)))
      .fold()
  }, [])
}

export const useChecklistFormattedFormData = () => {
  const checklist = useChecklist()
  const maxQuestions = useMaxQuestions()

  return React.useMemo(() => {
    const data = {
      totalQuestions: checklist?.questions?.length || 0,
      byPassLimit: checklist?.questions?.length > maxQuestions,
      maxQuestions
    }

    if (!checklist || !checklist.id) return data

    return {
      ...data,
      ...checklist,
      history: [],
      type: checklist.type || {},
      categories: compose(formatCategories, sortReplies)(checklist.questions)
    }
  }, [checklist, maxQuestions])
}
